import CryptoJS from 'crypto-js';

// Master encryption salt - must match ENCRYPTION_KEY in backend .env
const ENCRYPTION_SALT = 'PhoenixSecureKey2025';

/**
 * Derive a 256-bit key using HMAC-SHA256 (matches PHP hash_hmac('sha256', ...))
 */
const deriveKey = (keyString: string): CryptoJS.lib.WordArray => {
	return CryptoJS.HmacSHA256(keyString, ENCRYPTION_SALT);
};

/**
 * Legacy MD5 key derivation (kept for backward compatibility during migration)
 */
export const hashMD5 = (data: string): CryptoJS.lib.WordArray => {
	const md5Key = CryptoJS.MD5(data).toString();
	return CryptoJS.enc.Utf8.parse(md5Key);
};

/**
 * Encrypt data using AES-256-CBC with HMAC-SHA256 derived key and random IV.
 * Output: hex(IV + ciphertext) - matches PHP encryptDataFunc format.
 */
export const encryptData = (data: object, key: string): string => {
	const KEY = deriveKey(key);

	// Generate random 16-byte IV
	const iv = CryptoJS.lib.WordArray.random(16);

	const jsonString = JSON.stringify(data);
	const encrypted = CryptoJS.AES.encrypt(jsonString, KEY, {
		iv: iv,
		mode: CryptoJS.mode.CBC,
		padding: CryptoJS.pad.Pkcs7,
	});

	// Prepend IV to ciphertext, then hex-encode
	const ivBytes = iv;
	const ciphertextBytes = CryptoJS.enc.Base64.parse(encrypted.toString());
	const combined = ivBytes.concat(ciphertextBytes);

	return CryptoJS.enc.Hex.stringify(combined);
};

/**
 * Decrypt data from hex-encoded ciphertext.
 * Supports both new format (random IV prepended, HMAC-SHA256 key) and
 * legacy format (zero IV, MD5 key).
 */
export const decryptData = (encryptedHex: string, key: string) => {
	const rawData = CryptoJS.enc.Hex.parse(encryptedHex);

	// Try new format first (HMAC-SHA256 key, random IV prepended)
	try {
		const KEY = deriveKey(key);
		const iv = CryptoJS.lib.WordArray.create(rawData.words.slice(0, 4), 16);
		const ciphertext = CryptoJS.lib.WordArray.create(
			rawData.words.slice(4),
			rawData.sigBytes - 16,
		);

		const ciphertextBase64 = CryptoJS.enc.Base64.stringify(ciphertext);

		const decrypted = CryptoJS.AES.decrypt(ciphertextBase64, KEY, {
			iv: iv,
			mode: CryptoJS.mode.CBC,
			padding: CryptoJS.pad.Pkcs7,
		});

		const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
		if (decryptedText) {
			const result = JSON.parse(decryptedText);
			if (result !== null) {
				return result;
			}
		}
	} catch {
		// Fall through to legacy decryption
	}

	// Fallback to legacy format (MD5 key, zero IV)
	return decryptDataLegacy(encryptedHex, key);
};

/**
 * Legacy decryption for backward compatibility.
 * Uses MD5 key derivation and zero IV.
 */
const decryptDataLegacy = (encryptedHex: string, key: string) => {
	const LEGACY_IV = CryptoJS.enc.Utf8.parse(
		'\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00',
	);
	const KEY = hashMD5(key);

	const encryptedBase64 = CryptoJS.enc.Hex.parse(encryptedHex).toString(CryptoJS.enc.Base64);

	const decrypted = CryptoJS.AES.decrypt(encryptedBase64, KEY, {
		iv: LEGACY_IV,
		mode: CryptoJS.mode.CBC,
		padding: CryptoJS.pad.Pkcs7,
	});

	const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
	return JSON.parse(decryptedText);
};
